/**
 * Users management UI — used both standalone (/users) and embedded inside
 * the Organization page (/organization → Usuarios tab).
 *
 * No <AppHeader/Layout> here on purpose: the parent decides the page chrome.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Button, Table, Select, Space, Tag,
  Popconfirm, message, Spin, Empty, Modal, Form, Input,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { EXECUTOR_API } from '../../types';
import { useUser, UserRole } from '../../contexts/UserContext';
import { useAdminGuard } from '../../hooks/useAdminGuard';
import { buildAuthHeaders } from '../../utils/userStore';
import RoleBadge from '../common/RoleBadge';

const { Text } = Typography;

interface UserRecord {
  email: string;
  role: UserRole;
  created_at: string;
}

const UsersManagementPanel: React.FC = () => {
  const navigate = useNavigate();
  const { email: currentEmail, isAdmin } = useUser();
  const { loading: userLoading } = useAdminGuard();

  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [form] = Form.useForm();

  const fetchUsers = useCallback(async () => {
    if (!currentEmail) return;
    setLoading(true);
    try {
      const res = await fetch(`${EXECUTOR_API}/agent/builder/users`, {
        headers: buildAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json() as { users: UserRecord[] };
        setUsers(data.users || []);
      } else if (res.status === 403) {
        message.error('Acceso denegado. Solo los administradores pueden ver esta página.');
        navigate('/');
      }
    } catch {
      message.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, [currentEmail, navigate]);

  useEffect(() => {
    // useAdminGuard handles the redirect when isAdmin becomes false;
    // here we just fetch once we know the user is allowed.
    if (!userLoading && isAdmin) fetchUsers();
  }, [userLoading, isAdmin, fetchUsers]);

  const handleSetRole = async (email: string, role: UserRole) => {
    try {
      const res = await fetch(`${EXECUTOR_API}/agent/builder/users/${encodeURIComponent(email)}`, {
        method: 'PUT',
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ role }),
      });
      if (res.ok) {
        message.success(`Rol de ${email} actualizado a ${role}`);
        fetchUsers();
      } else {
        message.error('Error al actualizar rol');
      }
    } catch {
      message.error('Error de red');
    }
  };

  const handleDelete = async (email: string) => {
    try {
      const res = await fetch(`${EXECUTOR_API}/agent/builder/users/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: buildAuthHeaders(),
      });
      if (res.ok) {
        message.success(`Usuario ${email} eliminado`);
        fetchUsers();
      } else {
        message.error('Error al eliminar usuario');
      }
    } catch {
      message.error('Error de red');
    }
  };

  const handleAddUser = async (values: { email: string; role: UserRole }) => {
    await handleSetRole(values.email.trim().toLowerCase(), values.role);
    setAddModalOpen(false);
    form.resetFields();
  };

  const columns = [
    {
      title: 'Email', dataIndex: 'email', key: 'email',
      render: (email: string) => (
        <Text>{email} {email === currentEmail && <Tag color="green">Tú</Tag>}</Text>
      ),
    },
    {
      title: 'Rol', dataIndex: 'role', key: 'role', width: 160,
      render: (role: UserRole, record: UserRecord) => (
        <Select<UserRole>
          value={role} size="small" style={{ width: 120 }}
          disabled={record.email === currentEmail}
          onChange={(newRole) => handleSetRole(record.email, newRole)}
          options={[
            { value: 'admin', label: 'Admin' },
            { value: 'editor', label: 'Editor' },
            { value: 'viewer', label: 'Viewer' },
          ]}
        />
      ),
    },
    {
      title: 'Desde', dataIndex: 'created_at', key: 'created_at', width: 180,
      render: (date: string) => <Text type="secondary">{new Date(date).toLocaleDateString('es-ES')}</Text>,
    },
    {
      title: '', key: 'actions', width: 60,
      render: (_: unknown, record: UserRecord) =>
        record.email !== currentEmail ? (
          <Popconfirm
            title="¿Eliminar este usuario?"
            description="El usuario volverá a tener acceso de viewer por defecto."
            onConfirm={() => handleDelete(record.email)}
            okText="Eliminar" cancelText="Cancelar"
          >
            <Button type="text" danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        ) : null,
    },
  ];

  if (userLoading) {
    return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Text type="secondary" style={{ flex: 1 }}>
          Los usuarios con rol <RoleBadge role="admin" /> pueden gestionar agentes y usuarios.{' '}
          <RoleBadge role="editor" /> pueden crear y editar agentes.{' '}
          <RoleBadge role="viewer" /> tienen acceso de solo lectura.
        </Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
          Añadir usuario
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : users.length === 0 ? (
        <Empty description="No hay usuarios registrados" />
      ) : (
        <Table
          columns={columns}
          dataSource={users.map((u) => ({ ...u, key: u.email }))}
          pagination={false} size="middle"
        />
      )}

      <Modal
        title="Añadir usuario"
        open={addModalOpen}
        onCancel={() => { setAddModalOpen(false); form.resetFields(); }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleAddUser}>
          <Form.Item
            name="email" label="Email"
            rules={[
              { required: true, message: 'Introduce el email' },
              { type: 'email', message: 'Email inválido' },
            ]}
          >
            <Input placeholder="usuario@cubicup.com" />
          </Form.Item>
          <Form.Item name="role" label="Rol" initialValue="editor" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'admin', label: 'Admin' },
                { value: 'editor', label: 'Editor' },
                { value: 'viewer', label: 'Viewer' },
              ]}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setAddModalOpen(false); form.resetFields(); }}>Cancelar</Button>
              <Button type="primary" htmlType="submit">Añadir</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default UsersManagementPanel;
